
import React, { useState } from 'react';
import { Category, Assessment } from '@/types/assessment';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { CategoryCard } from './CategoryCard';
import { EmptyCategoriesList } from './EmptyCategoriesList';
import { useToast } from '@/hooks/use-toast';
import { deleteCategory } from '@/services/assessment';

interface CategoriesTabProps {
  categories: Category[];
  setCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  assessment?: Assessment | null;
  onContentChange?: () => void;
}

export function CategoriesTab({ categories, setCategories, assessment, onContentChange }: CategoriesTabProps) {
  const { toast } = useToast();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleAddCategory = () => {
    const newCategory: Category = {
      id: crypto.randomUUID(),
      name: '',
      description: '',
      questions: [],
      order: categories.length
    };
    setCategories([...categories, newCategory]);
    
    // Remove aggressive auto-save - let users make changes without immediate dialogs
  };

  const handleUpdateCategory = (categoryId: string, updates: Partial<Category>) => {
    setCategories(categories.map(category => 
      category.id === categoryId ? { ...category, ...updates } : category
    ));
    
    // Remove aggressive auto-save - let users make changes without immediate dialogs
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!assessment?.id) return;
    
    setIsDeleting(true);
    try {
      // Delete the category from the database
      await deleteCategory(assessment.id, categoryId);
      
      // Update local state only after successful database deletion
      setCategories(categories.filter(category => category.id !== categoryId));
      
      toast({
        title: "Category deleted",
        description: "The category has been successfully removed."
      });
    } catch (error) {
      console.error("Error deleting category:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete the category. Please try again."
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMoveCategory = (currentIndex: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= categories.length) return;
    
    reorderCategories(currentIndex, newIndex);
  };

  const reorderCategories = (fromIndex: number, toIndex: number) => {
    const newCategories = [...categories];
    const [movedCategory] = newCategories.splice(fromIndex, 1);
    newCategories.splice(toIndex, 0, movedCategory);
    
    // Update order values
    const updatedCategories = newCategories.map((category, index) => ({
      ...category,
      order: index
    }));
    
    setCategories(updatedCategories);
    
    // Remove aggressive auto-save - let users make changes without immediate dialogs
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    reorderCategories(draggedIndex, index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const sortedCategories = [...categories].sort((a, b) => 
    (a.order ?? 0) - (b.order ?? 0)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">Assessment Categories</h2>
          <p className="text-sm text-muted-foreground">
            Create and organize your assessment categories
          </p>
        </div>
        <Button onClick={handleAddCategory} disabled={isDeleting}>
          <Plus className="mr-2 h-4 w-4" />
          Add Category
        </Button>
      </div>

      {categories.length === 0 ? (
        <EmptyCategoriesList />
      ) : (
        <div className="space-y-4">
          {sortedCategories.map((category, index) => (
            <div
              key={category.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className="cursor-grab active:cursor-grabbing"
            >
              <CategoryCard
                category={category}
                index={index}
                totalCategories={categories.length}
                onUpdateCategory={handleUpdateCategory}
                onMoveCategory={handleMoveCategory}
                onDeleteCategory={handleDeleteCategory}
                isDeleting={isDeleting}
                dragHandleProps={{
                  onPointerDown: (e) => {
                    e.currentTarget.closest('[draggable]')?.setAttribute('draggable', 'true');
                  },
                  onPointerUp: (e) => {
                    e.currentTarget.closest('[draggable]')?.setAttribute('draggable', 'false');
                  }
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
